import OrderUserApiAgency from '@/api/OrderUserApiAgency';
import router from '@/router/index.router';
import OrderModalCandidate from './../components/order/OrderModalCandidate.vue';
import { Bus, useAside, useLoader, useModal } from '@orion.ui/orion';
import { pick } from 'lodash-es';
import { reactive } from 'vue';
import type OrderEntity from '@/entity/order/OrderEntity';
import TempWorkerAsideDetails from '@/components/temp-worker/TempWorkerAsideDetails.vue';
import useBusService from './BusService';

type AsideNestProps = {
	id: number,
	order?: OrderEntity,
  orderClientProposal?: IOrderClientProposalWithUserModel,
	tab?: string,
	highlightElement?: string,
	isValidatedCandidate?: Boolean,
}

type CandidateRating = {
  candidateId: number,
  numberOfRating: number,
  rating: number
}

type CandidateTalents = {
  candidateId: number,
  talents: ITalentModel[]
}

class CandidateService {
	private readonly state = reactive({
		ratingList: [] as CandidateRating[],
		talentsList: [] as CandidateTalents[],
	});

	get ratingList () { return this.state.ratingList; }

	get talentsList () { return this.state.talentsList; }

	getCandidateRating (candidateId: number): Undef<CandidateRating> {
		return this.state.ratingList.findByKey(candidateId, 'candidateId');
	}

	setCandidateRating (payload: CandidateRating) {
		const targetCandidate = this.getCandidateRating(payload.candidateId);

		if (targetCandidate) {
			Object.assign(targetCandidate, pick(payload, ['numberOfRating', 'rating']));
		} else {
			this.state.ratingList.push(pick(payload, ['candidateId', 'numberOfRating', 'rating']));
		}

		useBusService().emit(`${payload.candidateId}-list-item:ranking-update`, payload.rating);
	}

	getCandidateTalents (candidateId: number) {
		return this.state.talentsList.findByKey(candidateId, 'candidateId')?.talents ?? [];
	}

	setCandidateTalents (payload: CandidateTalents) {
		const targetCandidate = this.state.talentsList.findByKey(payload.candidateId, 'candidateId');
		if (targetCandidate) {
			targetCandidate.talents = payload.talents ?? [];
		} else {
			this.state.talentsList.push(pick(payload, ['candidateId', 'talents']));
		}
	}

	openAsideProfile (nestedProps: AsideNestProps) {
		useAside({
			Nested: TempWorkerAsideDetails,
			NestedProps: nestedProps,
			size: 'lg',
		});
	}

	setCandidateData (candidateData: CandidateRating & CandidateTalents) {
		this.setCandidateRating(candidateData);
		this.setCandidateTalents(candidateData);
	}

	async addCandidatesAsync (order: OrderEntity) {
		useModal({
			Nested: OrderModalCandidate,
			NestedProps: {
				orderId: Number(router.currentRoute.value.params.id),
				order: order,
			},
			customClass: 'order-modal-candidate',
			size: 'xl',
			events: {
				'add-candidates': async (Modal, payload) => {
					try {
						useLoader().show(`Ajout des candidats sélectionnés...`);

						await OrderUserApiAgency.add({
							userIds: (payload as Armado.TempWorkerListItemEntityType[]).map(x => x.id),
							orderId: order.id,
						});

						await Modal.close();

						if (this.inOrder()) {
							Bus.emit('OrderDetailsPipelineSetupService:added');
							Bus.emit('OrderDetailsSetupService:refresh-entity');
						} else {
							Bus.emit('TempWorkerPagedListViewSetupService:added');
						}
					} finally {
						useLoader().hide();
					}
				},
			},
		});
	}

	async rejectAsync (...args: Parameters<typeof OrderUserApiAgency.reject>) {
		const { data } = await OrderUserApiAgency.reject(...args);
		return data;
	}

	async rejectCancelAsync (...args: Parameters<typeof OrderUserApiAgency.rejectCancel>) {
		const { data } = await OrderUserApiAgency.rejectCancel(...args);
		return data;
	}

	inOrder () {
		return router.currentRoute.value.name === 'OrderDetailsPipeline';
	}

	inTalentPools () {
		return router.currentRoute.value.name === 'ToolboxTalentPool';
	}

	inCandidates () {
		return router.currentRoute.value.name === 'TempWorker';
	}
}

const candidateServiceSingleton = new CandidateService();

export default function useCandidateService () {
	return candidateServiceSingleton;
}
